import { deepMergeShape } from './deepMerge'
import { clamp, commonBox, screenToPage } from './geometry'
import { HistoryManager } from './history'
import {
  clearPageImpl,
  findShapeAncestorImpl,
  reparentShapesImpl,
  unparentShapesImpl,
} from './storeOperations'
import type {
  AssetId,
  Box,
  Camera,
  EditorAsset,
  EditorShape,
  Point,
  SelectSubState,
  ShapeId,
  Size,
  ToolId,
  ToolStyles,
} from './types'
import {
  DEFAULT_GRID_SIZE,
  DEFAULT_TOOL_STYLES,
  MAX_ZOOM,
  MIN_ZOOM,
} from './types'

type Listener = () => void

export class EditorStore {
  // Core state
  shapes = new Map<ShapeId, EditorShape>()
  assets = new Map<AssetId, EditorAsset>()
  camera: Camera = { x: 0, y: 0, zoom: 1 }
  selectedShapeIds = new Set<ShapeId>()
  currentTool: ToolId = 'marker'
  selectSubState: SelectSubState = 'idle'
  toolStyles: ToolStyles = { ...DEFAULT_TOOL_STYLES }
  viewportSize: Size = { width: 800, height: 600 }
  gridSize = DEFAULT_GRID_SIZE
  isGridMode = true
  currentPagePoint: Point = { x: 0, y: 0 }
  stageRef: { current: unknown } | null = null

  isDrawing = false
  cropRect: Box | null = null

  readonly history = new HistoryManager()
  private listeners = new Set<Listener>()
  private version = 0
  private shapesCache: EditorShape[] | null = null
  private sortedShapesCache: EditorShape[] | null = null

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getVersion() {
    return this.version
  }

  /** @internal Used by storeOperations. */
  notify() {
    this.shapesCache = null
    this.sortedShapesCache = null
    this.version++
    for (const l of this.listeners) l()
  }

  setIsDrawing(drawing: boolean): this {
    if (this.isDrawing === drawing) return this
    this.isDrawing = drawing
    this.notify()
    return this
  }

  getShape<T extends EditorShape = EditorShape>(id: ShapeId): T | undefined {
    return this.shapes.get(id) as T | undefined
  }

  getAllShapes(): EditorShape[] {
    if (!this.shapesCache) {
      this.shapesCache = [...this.shapes.values()]
    }
    return this.shapesCache
  }

  getSortedShapes(): EditorShape[] {
    if (!this.sortedShapesCache) {
      this.sortedShapesCache = this.getAllShapes()
        .slice()
        .sort((a, b) => a.index.localeCompare(b.index))
    }
    return this.sortedShapesCache
  }

  getShapesByType<T extends EditorShape>(type: T['type']): T[] {
    return this.getAllShapes().filter(s => s.type === type) as T[]
  }

  /** Get sorted child IDs for a parent */
  getChildIds(parentId: ShapeId): ShapeId[] {
    return this.getAllShapes()
      .filter(s => s.parentId === parentId)
      .sort((a, b) => a.index.localeCompare(b.index))
      .map(s => s.id)
  }

  createShape<T extends EditorShape>(
    partial: Omit<T, 'opacity' | 'rotation' | 'isLocked' | 'index'> & {
      opacity?: number
      rotation?: number
      isLocked?: boolean
      index?: string
    },
  ): this {
    const shape = {
      opacity: 1,
      rotation: 0,
      isLocked: false,
      ...partial,
      index: partial.index || this.getNextIndex(),
    } as unknown as T

    const prev = this.shapes.get(shape.id)

    this.history.execute({
      do: () => {
        this.shapes.set(shape.id, shape)
        this.notify()
      },
      undo: () => {
        if (prev) {
          this.shapes.set(shape.id, prev)
        } else {
          this.shapes.delete(shape.id)
        }
        this.selectedShapeIds.delete(shape.id)
        this.notify()
      },
    })

    return this
  }

  updateShape<T extends EditorShape>(
    partial: { id: ShapeId; type: string } & Partial<Omit<T, 'props'>> & {
        props?: Partial<T['props']>
      },
    options?: { squashing?: boolean; ephemeral?: boolean; tag?: string },
  ): this {
    const existing = this.shapes.get(partial.id)
    if (!existing) return this

    const updated = deepMergeShape(existing, partial as Partial<EditorShape>)

    const applyUpdate = () => {
      this.shapes.set(partial.id, updated)
      this.notify()
    }

    if (options?.ephemeral) {
      this.history.applyEphemeral(applyUpdate)
    } else {
      const prev = existing
      this.history.execute({
        do: () => {
          this.shapes.set(partial.id, updated)
          this.notify()
        },
        undo: () => {
          this.shapes.set(partial.id, prev)
          this.notify()
        },
        squash: options?.squashing,
        tag: options?.tag ?? (options?.squashing ? partial.id : undefined),
      })
    }

    return this
  }

  updateShapes(
    updates: ({ id: ShapeId; type: string } & Partial<EditorShape>)[],
    options?: { squashing?: boolean; tag?: string },
  ): this {
    const validUpdates = updates.filter(u => this.shapes.has(u.id))
    if (validUpdates.length === 0) return this

    const prevShapes = validUpdates.map(u => this.shapes.get(u.id)!)
    const newShapes = validUpdates.map((u, i) =>
      deepMergeShape(prevShapes[i], u),
    )

    const tag =
      options?.tag ??
      (options?.squashing ? validUpdates.map(u => u.id).join(',') : undefined)

    this.history.execute({
      do: () => {
        for (const shape of newShapes) {
          if (shape) this.shapes.set(shape.id, shape)
        }
        this.notify()
      },
      undo: () => {
        for (const shape of prevShapes) {
          if (shape) this.shapes.set(shape.id, shape)
        }
        this.notify()
      },
      squash: options?.squashing,
      tag,
    })

    return this
  }

  deleteShape(id: ShapeId | EditorShape): this {
    const shapeId = typeof id === 'string' ? id : id.id
    return this.deleteShapes([shapeId])
  }

  deleteShapes(ids: ShapeId[]): this {
    // Also collect children of deleted shapes
    const toDelete = new Set<ShapeId>()
    const collectChildren = (id: ShapeId) => {
      toDelete.add(id)
      for (const shape of this.shapes.values()) {
        if (shape.parentId === id) collectChildren(shape.id)
      }
    }
    for (const id of ids) collectChildren(id)

    const deleted = new Map<ShapeId, EditorShape>()
    for (const id of toDelete) {
      const shape = this.shapes.get(id)
      if (shape) deleted.set(id, shape)
    }

    if (deleted.size === 0) return this

    this.history.execute({
      do: () => {
        for (const id of deleted.keys()) {
          this.shapes.delete(id)
          this.selectedShapeIds.delete(id)
        }
        this.notify()
      },
      undo: () => {
        for (const [id, shape] of deleted) {
          this.shapes.set(id, shape)
        }
        this.notify()
      },
    })

    return this
  }

  select(...ids: ShapeId[]): this {
    this.selectedShapeIds.clear()
    for (const id of ids) this.selectedShapeIds.add(id)
    this.notify()
    return this
  }

  toggleSelection(id: ShapeId): this {
    if (this.selectedShapeIds.has(id)) {
      this.selectedShapeIds.delete(id)
    } else {
      this.selectedShapeIds.add(id)
    }
    this.notify()
    return this
  }

  getSelectedShapes(): EditorShape[] {
    return [...this.selectedShapeIds]
      .map(id => this.shapes.get(id))
      .filter((s): s is EditorShape => s != null)
  }

  getOnlySelectedShape(): EditorShape | undefined {
    if (this.selectedShapeIds.size !== 1) return undefined
    const [id] = this.selectedShapeIds
    return this.shapes.get(id)
  }

  getShapeParent(id: ShapeId): EditorShape | undefined {
    const shape = this.shapes.get(id)
    if (!shape?.parentId) return undefined
    return this.shapes.get(shape.parentId)
  }

  findShapeAncestor(
    shape: EditorShape,
    predicate: (s: EditorShape) => boolean,
  ): EditorShape | undefined {
    return findShapeAncestorImpl(this, shape, predicate)
  }

  reparentShapes(shapeIds: ShapeId[], newParentId: ShapeId): this {
    reparentShapesImpl(this, shapeIds, newParentId)
    return this
  }

  unparentShapes(shapeIds: ShapeId[]): this {
    unparentShapesImpl(this, shapeIds)
    return this
  }

  createAsset(asset: EditorAsset): this {
    this.assets.set(asset.id, asset)
    this.notify()
    return this
  }

  createAssets(assets: EditorAsset[]): this {
    for (const asset of assets) this.assets.set(asset.id, asset)
    this.notify()
    return this
  }

  getAsset(id: AssetId): EditorAsset | undefined {
    return this.assets.get(id)
  }

  deleteAssets(ids: AssetId[]): this {
    for (const id of ids) {
      const asset = this.assets.get(id)
      if (asset?.src.startsWith('blob:')) URL.revokeObjectURL(asset.src)
      this.assets.delete(id)
    }
    this.notify()
    return this
  }

  setCropRect(rect: Box | null): this {
    this.cropRect = rect
    this.notify()
    return this
  }

  setTool(toolId: ToolId): this {
    if (this.currentTool === 'crop' && toolId !== 'crop') {
      this.cropRect = null
    }
    this.currentTool = toolId
    this.selectSubState = 'idle'
    this.notify()
    return this
  }

  setSelectSubState(state: SelectSubState): this {
    this.selectSubState = state
    this.notify()
    return this
  }

  setToolStyle<K extends keyof ToolStyles>(key: K, value: ToolStyles[K]): this {
    this.toolStyles = { ...this.toolStyles, [key]: value }
    this.notify()
    return this
  }

  setCamera(camera: Partial<Camera>): this {
    this.camera = {
      ...this.camera,
      ...camera,
      zoom: clamp(camera.zoom ?? this.camera.zoom, MIN_ZOOM, MAX_ZOOM),
    }
    this.notify()
    return this
  }

  setViewportSize(size: Size): this {
    this.viewportSize = size
    this.notify()
    return this
  }

  /**
   * Apply a wheel-event delta to the camera.
   * `pointer` is in screen (viewport) coordinates.
   */
  applyWheelDelta(
    pointer: Point,
    deltaX: number,
    deltaY: number,
    zoomMode: boolean,
  ): this {
    if (zoomMode) {
      const cam = this.camera
      const direction = deltaY > 0 ? -1 : 1
      const factor = 1.05
      const newZoom = direction > 0 ? cam.zoom * factor : cam.zoom / factor
      const mousePointTo = screenToPage(pointer, cam)
      return this.setCamera({
        zoom: newZoom,
        x: pointer.x / newZoom - mousePointTo.x,
        y: pointer.y / newZoom - mousePointTo.y,
      })
    }
    return this.setCamera({
      x: this.camera.x - deltaX / this.camera.zoom,
      y: this.camera.y - deltaY / this.camera.zoom,
    })
  }

  zoomIn(): this {
    return this.setCamera({ zoom: this.camera.zoom * 1.25 })
  }
  zoomOut(): this {
    return this.setCamera({ zoom: this.camera.zoom / 1.25 })
  }

  zoomToBounds(bounds: Box): this {
    const { width: vw, height: vh } = this.viewportSize
    const padding = 40
    const zoom = clamp(
      Math.min(
        (vw - padding * 2) / bounds.width,
        (vh - padding * 2) / bounds.height,
      ),
      MIN_ZOOM,
      MAX_ZOOM,
    )
    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    return this.setCamera({
      x: vw / 2 / zoom - cx,
      y: vh / 2 / zoom - cy,
      zoom,
    })
  }

  zoomToSelection(): this {
    const shapes = this.getSelectedShapes()
    if (shapes.length === 0) return this
    const boxes = shapes.map(s => this.getShapeBounds(s))
    return this.zoomToBounds(commonBox(boxes))
  }

  zoomToFit(): this {
    const shapes = this.getAllShapes()
    if (shapes.length === 0) return this
    const boxes = shapes.map(s => this.getShapeBounds(s))
    return this.zoomToBounds(commonBox(boxes))
  }

  // --- Bounds ---
  getShapeBounds(shape: EditorShape): Box {
    const props = shape.props as { w?: number; h?: number }
    const w = props.w ?? 0
    const h = props.h ?? 0

    // Accumulate parent transforms
    let x = shape.x
    let y = shape.y
    let current = shape.parentId ? this.shapes.get(shape.parentId) : undefined
    while (current) {
      x += current.x
      y += current.y
      current = current.parentId ? this.shapes.get(current.parentId) : undefined
    }

    return { x, y, width: w, height: h }
  }

  /** Hit test: find topmost shape at a page point */
  getShapeAtPoint(
    point: Point,
    options?: {
      margin?: number
      filter?: (shape: EditorShape) => boolean
    },
  ): EditorShape | undefined {
    const margin = options?.margin ?? 0
    const shapes = this.getAllShapes()
      .filter(s => !options?.filter || options.filter(s))
      .reverse() // top-most first
    for (const shape of shapes) {
      const bounds = this.getShapeBounds(shape)
      if (
        point.x >= bounds.x - margin &&
        point.x <= bounds.x + bounds.width + margin &&
        point.y >= bounds.y - margin &&
        point.y <= bounds.y + bounds.height + margin
      ) {
        return shape
      }
    }
    return undefined
  }

  private indexCounter = 0
  private getNextIndex(): string {
    this.indexCounter++
    return String(this.indexCounter).padStart(6, '0')
  }

  setCurrentPagePoint(point: Point): this {
    this.currentPagePoint = point
    // Don't notify for pointer moves (too frequent)
    return this
  }

  isInAny(...states: string[]): boolean {
    for (const state of states) {
      const [tool, subState] = state.split('.')
      if (this.currentTool === tool) {
        if (!subState || this.selectSubState === subState) return true
      }
    }
    return false
  }

  /** Clear all shapes from the page */
  clearPage(): this {
    clearPageImpl(this)
    return this
  }

  /** Revoke all blob URLs held by assets. Call on unmount. */
  dispose(): void {
    for (const asset of this.assets.values()) {
      if (asset.src.startsWith('blob:')) URL.revokeObjectURL(asset.src)
    }
    this.shapes.clear()
    this.assets.clear()
    this.selectedShapeIds.clear()
    this.shapesCache = null
    this.listeners.clear()
  }
}
